/**
 * Nieuwe klant koppelen aan een account of Gripp-bedrijf zonder account (Joris 1 okt 2026), in de
 * stijl van "Koppel aan account". Gekoppeld neemt de nieuwe klant dat account/bedrijf over in het
 * budget: omzet, offertes en lopend werk tellen dan bij de nieuwe klant en het account staat niet
 * meer apart in de lijst (niets dubbel). Alleen het budget wordt aangepast, niets naar Airtable of
 * Gripp. Accounts met eigen budget zijn niet te kiezen. Vangnet: undo-toast (terug naar de vorige
 * koppeling).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { koppelDoelenNieuw, koppelSuggestie } from './budgetLogica';

/** Koppelen of ontkoppelen met undo; ook gebruikt door de ontkoppel-knop in de tabel. */
export async function wijzigKoppeling(regel, doel, jaar, herlaad) {
  const uit = await budgetApi.koppelNieuweKlant(regel.sleutel, doel ? doel.sleutel : null, regel.versie, jaar);
  await herlaad();
  if (uit?.gewijzigd === false) return uit;
  const tekst = doel ? `${regel.naam} gekoppeld aan ${doel.naam}` : `${regel.naam} ontkoppeld`;
  biedOngedaanAan(tekst, async () => {
    await budgetApi.koppelNieuweKlant(regel.sleutel, uit.vorige, uit.versie, jaar);
    await herlaad();
  }, { eigenKnop: true });
  return uit;
}

export default function NieuwKoppelDialoog({ regel, regels, jaar, onClose, onGekoppeld }) {
  // Accounts eerst, dan bedrijven zonder account (groupBy wil gesorteerde groepen).
  const doelen = useMemo(() => koppelDoelenNieuw(regels)
    .sort((a, b) => Number(a.soort === 'zonder') - Number(b.soort === 'zonder')), [regels]);
  const suggestie = useMemo(() => (regel ? koppelSuggestie(regel.naam, doelen) : null), [regel, doelen]);
  const [keuze, setKeuze] = useState(null);
  const [bezig, setBezig] = useState(false);
  useEffect(() => { setKeuze(suggestie); }, [suggestie]);
  if (!regel) return null;

  const koppel = async () => {
    if (!keuze) return;
    setBezig(true);
    try {
      await wijzigKoppeling(regel, keuze, jaar, onGekoppeld);
      onClose();
    } catch (e) {
      toast.error(`Koppelen mislukt: ${e.message}`);
      if (e.status === 409) await onGekoppeld();
    } finally { setBezig(false); }
  };

  return (
    <Dialog open onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Koppel {regel.naam}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          Staat de klant intussen in Gripp of Airtable? Kies het account of het Gripp-bedrijf zonder account.
        </Typography>
        <Autocomplete
          options={doelen}
          value={keuze}
          onChange={(_, v) => setKeuze(v)}
          getOptionLabel={(a) => a.naam || ''}
          isOptionEqualToValue={(a, b) => a.sleutel === b.sleutel}
          groupBy={(a) => (a.soort === 'zonder' ? 'Gripp-bedrijf zonder account' : 'Account')}
          filterOptions={(opties, { inputValue }) => {
            const z = inputValue.trim().toLowerCase();
            return !z ? opties : opties.filter((a) => [a.naam, ...(a.bedrijfsnamen || [])]
              .some((n) => (n || '').toLowerCase().includes(z)));
          }}
          renderOption={(props, a) => (
            <li {...props} key={a.sleutel}>
              <Box>
                <Typography sx={{ fontSize: 14 }}>{a.naam}</Typography>
                {a.bedrijfsnamen?.length > 0 && a.soort !== 'zonder' && (
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{a.bedrijfsnamen.join(', ')}</Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(p) => <TextField {...p} label="Account of bedrijf" size="small" autoFocus
            helperText={suggestie && keuze?.sleutel === suggestie.sleutel ? 'Voorstel op basis van de naam' : 'Accounts met eigen budget staan er niet bij'} />}
        />
        {keuze && (
          <Typography sx={{ fontSize: 13.5, color: '#475569', mt: 1 }}>
            De omzet, offertes en het lopende werk van {keuze.naam} tellen daarna bij {regel.naam};
            {' '}{keuze.naam} staat dan niet meer apart in de lijst. Het target van {regel.naam} blijft staan.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={koppel} disabled={!keuze || bezig}
          sx={{ textTransform: 'none', fontWeight: 700 }}>{bezig ? 'Bezig…' : 'Koppel'}</Button>
      </DialogActions>
    </Dialog>
  );
}
